import '@fortawesome/fontawesome-svg-core/styles.css'
import '@szhsin/react-menu/dist/index.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-quill/dist/quill.snow.css'
import 'react-toastify/dist/ReactToastify.css'
import 'styles/main.css'

import { useContext, useEffect, useMemo } from 'react'
import ReactGA from 'react-ga4'
import TagManager from 'react-gtm-module'
import { hotjar } from 'react-hotjar'
import { IntlProvider } from 'react-intl'
import { Bounce, ToastContainer } from 'react-toastify'

import { config } from '@fortawesome/fontawesome-svg-core'
import { ThemeProvider } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/nextjs'
import { AuthContext, AuthProvider } from 'context/AuthContext'
import { DialogProvider } from 'context/DialogContext'
import { OnboardingProvider } from 'context/OnboardingContext'
import { PermissionsProvider } from 'context/PermissionsContext'
import { ReportProvider } from 'context/ReportContext'
import ptBR from 'date-fns/locale/pt-BR'
import { ChangelogProvider } from 'features/Changelog/context/ChangelogContext'
import { SessionProvider } from 'next-auth/react'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import NextNProgress from 'nextjs-progressbar'
import Layout from 'templates/Layout/Layout'

import ServiceWorkerRegister from 'components/ServiceWorkerRegister'
import { toastIcon } from 'components/Toast/toastIcons'

import { initMixpanel } from 'utils/mixpanel'
import 'utils/yupLocale'

import { theme } from 'styles/theme'

const TRACKING_ID = 'G-FS1NXR8N9Z'

import Brazil from '../../content/locales/br.json'
import English from '../../content/locales/en.json'

config.autoAddCss = false

// SC-11130: no Pages Router o Next captura erro de render no próprio container
// e mostra a página de erro, então ele nunca chega ao handler global do Sentry.
// O boundary registra o erro e relança no fallback: o Next segue tratando como
// sempre e a tela de erro que o usuário vê não muda.
const rethrowToNext = ({ error }: { error: unknown }): never => {
  throw error
}

export type MerchantProps = {
  merchant_user_uuid: string
  merchant_uuid: string
}

function MyApp({ Component, pageProps }: AppProps) {
  const tagManagerArgs = {
    gtmId: 'GTM-P7GRR3JZ'
  }

  const { signOut } = useContext(AuthContext)
  const { locale, defaultLocale } = useRouter()

  const messages = useMemo(() => {
    switch (locale) {
      case 'br':
        return Brazil
      case 'en':
        return English
      default:
        return Brazil
    }
  }, [locale])

  const intlLocale = locale === 'br' ? 'pt-BR' : locale

  if (process.browser && process.env.NEXT_PUBLIC_APP_STAGE === 'production') {
    TagManager.initialize(tagManagerArgs)
  }

  // SC-10692: o Mixpanel era inicializado apenas no Layout, que envolve só a
  // área logada. Páginas deslogadas — /registrar em primeiro lugar — ficavam
  // sem lib carregada, e o guard `if (!__loaded) return` do signupTracking
  // descartava todos os eventos do cadastro em silêncio: o GA4 registrou 165
  // cadastros em 30 dias contra 13 no Mixpanel. Inicializar aqui cobre o app
  // inteiro; o identify continua no Layout, onde o usuário já é conhecido.
  useEffect(() => {
    initMixpanel()
  }, [])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_STAGE === 'production') {
      ReactGA.initialize(TRACKING_ID)
      ReactGA.send({
        hitType: 'pageview',
        page: '/landingpage',
        title: 'Landing Page'
      })
      hotjar.initialize(3349324, 6)
    }
  }, [])

  return (
    <Sentry.ErrorBoundary fallback={rethrowToNext}>
      <SessionProvider session={pageProps.session}>
        <ThemeProvider theme={theme}>
          <IntlProvider
            messages={messages}
            locale={intlLocale as string}
            defaultLocale={defaultLocale}
          >
            <GoogleOAuthProvider clientId="381361225351-ssaahotgq41hbfp1fi8tsjlaib51pg9e.apps.googleusercontent.com">
              <ServiceWorkerRegister />
              {/* SC-10694: removida a tag <GoogleAnalytics gaMeasurementId=
                "5FECSTTR66" />. O valor não tem o prefixo `G-` de measurement
                ID e não corresponde a nenhuma das três propriedades da conta
                (verificado via API do GA4: todo o tráfego chega por um único
                stream, 6440540792). A tag carregava um gtag.js a mais e
                enviava para o vazio. O GA4 em uso é o GTM-P7GRR3JZ acima. */}
              <ToastContainer
                position="top-right"
                autoClose={3000}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
                icon={toastIcon}
                className="cb-toast-container"
                toastClassName="cb-toast"
                bodyClassName="cb-toast-body"
                progressClassName="cb-toast-progress"
              />
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <AuthProvider>
                  <PermissionsProvider>
                    <DialogProvider>
                      <OnboardingProvider>
                        <ReportProvider>
                          <ChangelogProvider>
                            <Layout>
                              <Component {...pageProps} signOut={signOut} />
                            </Layout>
                          </ChangelogProvider>
                        </ReportProvider>
                      </OnboardingProvider>
                    </DialogProvider>
                  </PermissionsProvider>
                </AuthProvider>
              </LocalizationProvider>

              <NextNProgress
                color={theme.palette.primary.main}
                height={3}
                showOnShallow={true}
                startPosition={0.3}
                stopDelayMs={201}
              />
            </GoogleOAuthProvider>
          </IntlProvider>
        </ThemeProvider>
      </SessionProvider>
    </Sentry.ErrorBoundary>
  )
}

export default MyApp
